import { Accordion, TabPanel } from '../';
import { MultiPanelElement } from '../multi-panel-element';

/**
 * Takes a structure like
 * ```html
 * div.js-accordion-tabs
 *   h2.js-accordion__title.js-tabs__title(data-selected="true")
 *   div.js-accordion__content.js-tabs__content
 *     //- arbitrary content
 *   h2.js-accordion__title.js-tabs__title
 *   div.js-accordion__content.js-tabs__content
 * ```
 * While the top container class depends on how you initiate the element, the "inner" "js-*" classes
 * need to be set for both the TabPanel and the Accordion element so they can be created as needed.
 * The outer contained should in turn _not_ contain the js-* base class for these elements!
 * As for styling it makes most sense to add both styling classes for TabPanel and Accordion.
 *
 * ## API
 * const a = new AccordionTabs(document.querySelector('.js-accordion-tabs'), 1180, 'accordion', 'tabs')
 * a.activePanel // div.js-accordion > div, getter, setter
 * a.destroy() // revert changes made by this component
 */
class AccordionTabs extends MultiPanelElement {

    /**
     * @param {HTMLDivElement} element
     * @param {number} breakpoint
     * @param {string} accordionBaseClass
     * @param {string} tabPanelBaseClass
     */
    constructor(element, breakpoint, accordionBaseClass, tabPanelBaseClass) {
        super(element);
        this._onResize = this._onResize.bind(this);
        this.destroy = this.destroy.bind(this);

        this._element = element;
        this._breakpoint = breakpoint;
        this._matchesBreakpoint = null;
        this._currentComponent = null;
        this._accordionBaseClass = accordionBaseClass;
        this._tabPanelBaseClass = tabPanelBaseClass;
        this._onResize();
        window.addEventListener('resize', this._onResize);
    }

    static get elementProperty() {
        return '_accordionTabs';
    }

    /**
     * The currently active panel element will usually be the title's next element sibling and have the role "tabpanel"
     * (i.e. the content container, not the panel container containing the title)
     * @returns {Element|null}
     */
    get activePanel() {
        if (this._currentComponent === null || !this._currentComponent.prototype) {
            return null;
        }
        if (this._currentComponent.prototype === Accordion.prototype
            || this._currentComponent.prototype === TabPanel.prototype) {
            return this._currentComponent.activePanel;
        }
        return null;
    }

    /**
     * @param {Element} element
     */
    set activePanel(element) {
        if (this._currentComponent.prototype === Accordion.prototype
            || this._currentComponent.prototype === TabPanel.prototype) {
            this._currentComponent.activePanel = element;
        }
    }

    get panels() {
        return this._currentComponent.panels;
    }

    _onResize() {
        const matchesBreakpoint = window.matchMedia(`only screen and ( min-width: ${this._breakpoint}px )`).matches;
        if (this._matchesBreakpoint === matchesBreakpoint) {
            return;
        }
        this._matchesBreakpoint = matchesBreakpoint;
        if (this._currentComponent !== null && this._currentComponent.destroy) {
            this._currentComponent.destroy();
        }
        if (matchesBreakpoint) {
            this._currentComponent = new TabPanel(this._element, this._tabPanelBaseClass);
        } else {
            this._currentComponent = new Accordion(this._element, this._accordionBaseClass);
        }
    }

    /**
     * Reverts all changes made
     */
    destroy() {
        if (this._currentComponent !== null) {
            this._currentComponent.destroy();
        }
        window.removeEventListener('resize', this._onResize);
    }
}

export {
    AccordionTabs,
};
